import { useEffect, useState, useCallback } from 'react';
import { apiFetch as get, apiPost as post } from '../api/client';
import { PageContainer } from '../components/PageContainer';

interface Candidate {
  title: string;
  thumbnailUrl: string | null;
}

interface ApprovedProduct {
  id: string;
  pushStatus: string;
  pushedAt: string | null;
  shopifyProductId: string | null;
  printfulProductId: string | null;
  candidate: Candidate;
}

const ACCENT = '#a0a8ff';
const CARD_BG = '#1a1a24';
const BORDER = '#2a2a3a';
const DANGER = '#ff6b6b';
const SUCCESS = '#6bffb8';
const WARNING = '#ffb347';

function StatusBadge({ status }: { status: string }) {
  const [color, bg] =
    status === 'complete'    ? [SUCCESS, SUCCESS + '22'] :
      status === 'failed'      ? [DANGER,  DANGER  + '22'] :
        status === 'in_progress' ? [WARNING, WARNING + '22'] :
          ['#888',  '#2a2a3a'];

  const label =
    status === 'complete'    ? 'pushed' :
      status === 'in_progress' ? 'pushing…' :
        status;

  return (
    <span style={{
      fontSize: 11, padding: '2px 8px', borderRadius: 999,
      background: bg, color, fontWeight: 700, letterSpacing: 0.5,
      textTransform: 'capitalize',
    }}>
      {label}
    </span>
  );
}

function ProductRow({
                      product,
                      onPush,
                    }: {
  product: ApprovedProduct;
  onPush: (id: string) => Promise<void>;
}) {
  const [pushing, setPushing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const canPush = product.pushStatus === 'pending' || product.pushStatus === 'failed';

  const handlePush = async () => {
    setPushing(true);
    setError(null);
    try {
      await onPush(product.id);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Push failed');
    } finally {
      setPushing(false);
    }
  };

  return (
    <div style={{
      background: CARD_BG, border: `1px solid ${BORDER}`,
      borderRadius: 10, overflow: 'hidden',
      display: 'grid',
      gridTemplateColumns: '80px 1fr auto',
      alignItems: 'center',
      gap: 16,
    }}>
      {/* Thumbnail */}
      <div style={{ width: 80, height: 80, background: '#0a0a12', flexShrink: 0 }}>
        {product.candidate.thumbnailUrl ? (
          <img
            src={product.candidate.thumbnailUrl}
            alt={product.candidate.title}
            style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
          />
        ) : (
          <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#444', fontSize: 11 }}>
            No image
          </div>
        )}
      </div>

      {/* Info */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6, padding: '12px 0', minWidth: 0 }}>
        <div style={{ fontSize: 14, color: '#ddd', fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {product.candidate.title}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <StatusBadge status={product.pushStatus} />
          {product.pushedAt && (
            <span style={{ fontSize: 11, color: '#555' }}>
              {new Date(product.pushedAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}
            </span>
          )}
        </div>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          {product.shopifyProductId && (
            <a
              href={`https://posternauts.myshopify.com/admin/products/${product.shopifyProductId}`}
              target="_blank"
              rel="noopener noreferrer"
              style={{ fontSize: 11, color: ACCENT, textDecoration: 'none' }}
            >
              Shopify ↗
            </a>
          )}
          {product.printfulProductId && (
            <a
              href={`https://www.printful.com/dashboard/sync/update?id=${product.printfulProductId}`}
              target="_blank"
              rel="noopener noreferrer"
              style={{ fontSize: 11, color: ACCENT, textDecoration: 'none' }}
            >
              Printful ↗
            </a>
          )}
        </div>
        {error && (
          <div style={{ fontSize: 11, color: DANGER }}>{error}</div>
        )}
      </div>

      {/* Action */}
      <div style={{ padding: '0 20px' }}>
        <button
          onClick={handlePush}
          disabled={!canPush || pushing}
          style={{
            padding: '8px 18px', borderRadius: 8, border: 'none',
            background: canPush && !pushing ? ACCENT : '#2a2a3a',
            color: canPush && !pushing ? '#111' : '#555',
            fontWeight: 700, fontSize: 13,
            cursor: canPush && !pushing ? 'pointer' : 'default',
            whiteSpace: 'nowrap',
            transition: 'all 0.15s',
          }}
        >
          {pushing ? 'Pushing…' : product.pushStatus === 'failed' ? 'Retry' : 'Push'}
        </button>
      </div>
    </div>
  );
}

export default function ApprovedProductsPage() {
  const [products, setProducts] = useState<ApprovedProduct[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchProducts = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await get<ApprovedProduct[]>('/approved');
      setProducts(data);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load approved products');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchProducts(); }, [fetchProducts]);

  const handlePush = async (id: string) => {
    // Optimistically mark as in_progress
    setProducts(prev =>
      prev.map(p => p.id === id ? { ...p, pushStatus: 'in_progress' } : p)
    );
    await post(`/push/${id}`, {});
    // Refresh to get final status + IDs
    await fetchProducts();
  };

  const pendingCount = products.filter(p => p.pushStatus === 'pending').length;
  const pushedCount  = products.filter(p => p.pushStatus === 'complete').length;

  return (
    <PageContainer>
    <div style={{ padding: 32, maxWidth: 900, margin: '0 auto', color: '#ccc' }}>
      <h1 style={{ margin: '0 0 8px', fontSize: 24, color: '#fff' }}>Approved Products</h1>
      <p style={{ margin: '0 0 28px', color: '#666', fontSize: 14 }}>
        {loading ? 'Loading…' : `${products.length} products — ${pushedCount} pushed, ${pendingCount} pending`}
      </p>

      {error && (
        <div style={{ color: DANGER, marginBottom: 20, fontSize: 14 }}>{error}</div>
      )}

      {loading ? (
        <div style={{ color: '#666', fontSize: 14 }}>Loading…</div>
      ) : products.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '60px 0', color: '#555', fontSize: 15 }}>
          No approved products yet. Approve candidates from the Queue.
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {products.map(p => (
            <ProductRow key={p.id} product={p} onPush={handlePush} />
          ))}
        </div>
      )}
    </div>
    </PageContainer>
  );
}